import { V86 } from 'v86';
import { ExecChannel } from './channel.js';
import { RangeDisk } from './disk.js';
import { isVmError, messageOf, vmError } from './errors.js';
import { CommandDone, GuestTerminal } from './terminal.js';
import { assetUrl, computeBootFingerprint, fingerprintMismatch, CMDLINE, VGA_MEMORY_MB, } from './manifest.js';
/**
 * Booting the VM: fetch what the machine is made of, build the emulator the
 * saved state was saved by, and wait for the guest agent to answer.
 *
 * The emulator options are spike/web/vm-options.js, unchanged. The one that
 * matters is `hda: { async: true }`: loaded synchronously, v86 embeds the
 * whole 192 MB disk in every saved state (232 MB); read by byte range, the
 * state holds only the blocks the guest wrote — 40 MB raw, 13 MB compressed
 * (docs/browser.md, Phase 0 results 1). It is also why `rootfs.img` is
 * served from R2 rather than from the app's own origin, which answers a
 * range request with the whole file.
 */
/**
 * Assets fetched whole, before the emulator exists. `rootfs.img` is not
 * among them: v86 reads it by range, on demand, for the life of the VM.
 *
 * They are fetched here rather than left to v86's own loader so the boot
 * screen can count real bytes, an abort can stop them, and a truncated file
 * is caught by its length instead of by a guest that boots strangely.
 */
const WHOLE_ASSETS = ['seabios.bin', 'vgabios.bin'];
/**
 * The kernel and initramfs, which only a cold boot reads.
 *
 * A restore does not: the saved state is the whole of guest RAM, the running
 * kernel included, and v86 writes it over whatever a `bzimage` would have
 * loaded. They were fetched on every visit anyway, 13.7 MB of the 25 MB a
 * first visit downloads, and on a real connection that was about five seconds
 * of the boot screen for bytes the restore threw away. Proved before it was
 * removed (2026-09-27): the production state restored with and without them
 * gives the same `uname`, the same `/proc/cmdline`, the same git and node and
 * the same disk, and is ready in the same 1.6 s. The fingerprint check does
 * not notice either way — it hashes the manifest, not the buffers — so a
 * state that no longer fits still refuses, and the cold boot that follows
 * fetches them.
 */
const COLD_BOOT_ASSETS = ['vmlinuz', 'initramfs'];
/**
 * How much of the boot bar the download owns. The rest is the restore, which
 * has no progress of its own and took 1.7–2.3 s in phase 0 against tens of
 * seconds of downloading on a cold cache.
 */
const ASSET_SHARE = 0.8;
/**
 * v86 compiles guest code the first time it runs it, and that JIT cache is
 * not part of a saved state, so the first ~10 commands after a restore run
 * 2–4× slower (docs/browser.md, Phase 0 results 5). This pays that cost once,
 * on the boot screen, in a throw-away repository on tmpfs, running exactly
 * the git plumbing the graph script and the grader use.
 */
const WARM_SCRIPT = [
    'set -e',
    'd=$(mktemp -d)',
    'cd "$d"',
    'git init -q -b main .',
    'git -c user.email=warm@lattice -c user.name=warm commit -q --allow-empty -m warm',
    'git status --porcelain >/dev/null',
    "git log --all --reflog --topo-order --format='%H %P %s' -n 300 >/dev/null",
    'git rev-list --all >/dev/null',
    'git for-each-ref >/dev/null',
    'git reflog show -n 40 >/dev/null',
    'cd /',
    'rm -rf "$d"',
].join('\n');
const WARM_TIMEOUT_MS = 60_000;
function requireSupport(memoryMb) {
    if (typeof WebAssembly !== 'object' || typeof WebAssembly.instantiate !== 'function') {
        throw vmError('unsupported', 'this browser has no WebAssembly, and EngineerOS runs Linux inside the tab');
    }
    if (typeof crypto === 'undefined' || crypto.subtle === undefined) {
        throw vmError('unsupported', 'this page needs Web Crypto, which browsers only offer over HTTPS');
    }
    // The guest's RAM is one wasm memory. Ask for it now, before a download:
    // a tab that cannot have it fails here in a millisecond instead of after
    // 13 MB.
    const pages = ((memoryMb + 64) * 1024 * 1024) / 65_536;
    try {
        new WebAssembly.Memory({ initial: pages });
    }
    catch (err) {
        throw vmError('unsupported', `this tab cannot allocate the ${memoryMb} MB the emulated machine needs`, err);
    }
}
function aborted(signal) {
    return signal?.aborted === true;
}
function throwIfAborted(signal) {
    if (aborted(signal))
        throw vmError('destroyed', 'the boot was aborted');
}
/**
 * No byte for this long and a download is presumed dead rather than slow.
 *
 * Measured 2026-10-01 against production: a connection that goes quiet
 * mid-body — a phone switching networks, a tab the OS froze and thawed, a
 * middlebox that drops the flow without a reset — leaves `reader.read()`
 * pending for ever. Nothing in the browser times it out, so before this the
 * boot screen sat on "Downloading Linux…" with no error and no way on. Twenty
 * seconds is several times the longest gap between chunks seen on a throttled
 * 1 Mbit/s line, so a slow download is never mistaken for a dead one.
 */
const STALL_MS = 20_000;
/** Attempts in a row that move no bytes before a download is given up on. */
const IDLE_ATTEMPTS = 4;
/**
 * Attempts in all, however much each one moved. A line that delivers a few
 * bytes and then stalls, over and over, would otherwise reset the idle count
 * for ever.
 */
const MAX_ATTEMPTS = 12;
/**
 * The pause before attempt n+1: 0.5, 1, 2, then 4 s, long enough for a phone
 * to find its network again. Paced off the stall limit so that the tests,
 * which shorten that, do not sit through real seconds of backoff.
 */
const retryDelay = (n, stallMs) => Math.min((stallMs / 40) * 2 ** (n - 1), stallMs / 5);
/** Errors no retry can fix: the server said no, or the file is not the one the manifest names. */
const final = new WeakSet();
function finalError(code, message) {
    const err = vmError(code, message);
    final.add(err);
    return err;
}
/** A 4xx other than a timeout or a rate limit is an answer; anything else may be the network. */
function refuseStatus(url, res) {
    if (res.ok)
        return;
    const message = `${url} answered ${res.status}`;
    if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429)
        throw finalError('download', message);
    throw vmError('download', message);
}
/**
 * Quiet this long when the tab comes back and a download is presumed to have
 * died with the freeze, rather than waited out for the whole `stallMs`.
 */
const WOKEN_QUIET_MS = 2_000;
function watch(outer, stallMs) {
    const controller = new AbortController();
    let stalled = false;
    let timer;
    let lastByte = Date.now();
    const stall = () => {
        stalled = true;
        controller.abort();
    };
    const onAbort = () => controller.abort();
    outer?.addEventListener('abort', onAbort, { once: true });
    // A phone that switches apps freezes the tab, and on 2026-10-01 a download
    // frozen mid-body never moved again after the thaw, even with the tab in
    // front (measured in Chrome against production). The stall timer catches
    // that eventually; this catches it the moment the learner is back.
    const doc = typeof document !== 'undefined' ? document : null;
    const onWake = (event) => {
        // `resume` is the thaw itself; a tab merely shown again counts only once it is visible.
        if (event.type === 'visibilitychange' && doc?.visibilityState !== 'visible')
            return;
        if (Date.now() - lastByte > WOKEN_QUIET_MS)
            stall();
    };
    doc?.addEventListener('resume', onWake);
    doc?.addEventListener('visibilitychange', onWake);
    const alive = () => {
        lastByte = Date.now();
        clearTimeout(timer);
        timer = setTimeout(stall, stallMs);
    };
    alive();
    return {
        signal: controller.signal,
        alive,
        stalled: () => stalled,
        done: () => {
            clearTimeout(timer);
            outer?.removeEventListener('abort', onAbort);
            doc?.removeEventListener('resume', onWake);
            doc?.removeEventListener('visibilitychange', onWake);
        },
    };
}
/**
 * Run `attempt` until it succeeds, the caller aborts, or the network has had
 * its chances, and fail with a `download` error the boot screen can offer a
 * retry for.
 *
 * Every failure of ours is coded. Before this, a connection that dropped
 * mid-body made `reader.read()` reject with a bare `TypeError: network error`,
 * which reached the boot screen uncoded: the learner was told Linux "started
 * but never answered" and that it was a bug in EngineerOS, and the funnel
 * counted it as `script` (three of them on 2026-10-01). `progress` is how far
 * the download has got, so an attempt that moved bytes earns a fresh set of
 * idle attempts.
 */
async function persist(url, { signal, stallMs }, progress, attempt) {
    let idle = 0;
    for (let n = 1;; n++) {
        throwIfAborted(signal);
        const before = progress();
        const w = watch(signal, stallMs);
        try {
            await attempt(w);
            return;
        }
        catch (err) {
            if (aborted(signal))
                throw vmError('destroyed', 'the boot was aborted');
            if (final.has(err))
                throw err;
            idle = progress() > before ? 0 : idle + 1;
            if (idle >= IDLE_ATTEMPTS || n >= MAX_ATTEMPTS) {
                if (w.stalled())
                    throw vmError('download', `${url} stopped arriving: nothing for ${stallMs / 1000} s, ${n} times`, err);
                throw isVmError(err) ? err : vmError('download', `could not fetch ${url}: ${messageOf(err)}`, err);
            }
        }
        finally {
            w.done();
        }
        await new Promise((resolve) => setTimeout(resolve, retryDelay(n, stallMs)));
    }
}
/** Where a 206 says its body starts, or null when it says nothing usable. */
function rangeStart(res) {
    const m = /^bytes (\d+)-/.exec(res.headers.get('content-range') ?? '');
    return m ? Number(m[1]) : null;
}
/**
 * Fetch one asset whole, reporting bytes as they arrive and refusing a body
 * that is not exactly the length the manifest promised.
 *
 * A failed or stalled attempt resumes from the last byte it had, by range:
 * every asset is named by its content hash, so the bytes at an offset can
 * never change between attempts. A server that ignores the range gets the
 * file from the start instead.
 */
async function download(url, file, fetching, onBytes) {
    const out = new Uint8Array(file.bytes);
    let offset = 0;
    let ranged = true;
    const restart = () => {
        onBytes(-offset);
        offset = 0;
    };
    // Progress is the most this download has ever held, not where it is now: a
    // restart from nothing that gets as far as before has not moved anything.
    let most = 0;
    await persist(url, fetching, () => most, async (w) => {
        if (!ranged && offset > 0)
            restart();
        const from = offset;
        const res = await fetch(url, from > 0 ? { signal: w.signal, headers: { Range: `bytes=${from}-` } } : { signal: w.signal });
        if (!(from > 0 && res.status === 416))
            refuseStatus(url, res);
        if (from > 0 && !(res.status === 206 && rangeStart(res) === from)) {
            // The range was ignored or answered from somewhere else: this body is
            // not the rest of the file. Start again from nothing, without a range.
            await res.body?.cancel().catch(() => undefined);
            ranged = false;
            restart();
            throw vmError('download', `${url} did not resume at byte ${from} (answered ${res.status})`);
        }
        if (res.body === null) {
            // No streaming body (an old browser, or a mocked response): take it
            // whole and report the bytes in one go.
            const rest = new Uint8Array(await res.arrayBuffer());
            if (from + rest.length !== out.length) {
                throw finalError('download', `${url} is ${from + rest.length} bytes, and the manifest says ${out.length}`);
            }
            out.set(rest, from);
            offset = most = out.length;
            onBytes(rest.length);
            return;
        }
        const reader = res.body.getReader();
        for (;;) {
            const { done, value } = await reader.read();
            if (done)
                break;
            w.alive();
            if (offset + value.length > out.length) {
                await reader.cancel().catch(() => undefined);
                throw finalError('download', `${url} is longer than the ${out.length} bytes the manifest says`);
            }
            out.set(value, offset);
            offset += value.length;
            most = Math.max(most, offset);
            onBytes(value.length);
        }
        if (offset !== out.length)
            throw vmError('download', `${url} stopped after ${offset} of ${out.length} bytes`);
    });
    return out.buffer;
}
/**
 * The emulator's wasm, whole. Its size is not in the manifest: it ships with
 * the app, from the app's own origin, so a retry simply starts it again.
 */
async function downloadWasm(url, fetching) {
    let result = null;
    let most = 0;
    await persist(url, fetching, () => most, async (w) => {
        const res = await fetch(url, { signal: w.signal });
        refuseStatus(url, res);
        if (res.body === null) {
            result = await res.arrayBuffer();
            return;
        }
        const reader = res.body.getReader();
        const chunks = [];
        let got = 0;
        for (;;) {
            const { done, value } = await reader.read();
            if (done)
                break;
            w.alive();
            chunks.push(value);
            got += value.length;
            most = Math.max(most, got);
        }
        const whole = new Uint8Array(got);
        let at = 0;
        for (const c of chunks) {
            whole.set(c, at);
            at += c.length;
        }
        result = whole.buffer;
    });
    return result;
}
function v86Options(manifest, opts) {
    const buffer = (name) => ({ buffer: opts.buffers.get(name) });
    const state = opts.buffers.get('state.bin.zst');
    return {
        // The emulator's own code, fetched beside the machine rather than after
        // it: left to v86, the request only starts once the constructor runs,
        // which is after every other download has finished.
        wasm_fn: async (imports) => (await WebAssembly.instantiate(await opts.wasm, imports)).instance.exports,
        memory_size: opts.memoryMb * 1024 * 1024,
        vga_memory_size: VGA_MEMORY_MB * 1024 * 1024,
        bios: buffer('seabios.bin'),
        vga_bios: buffer('vgabios.bin'),
        // Only a cold boot has these; see COLD_BOOT_ASSETS.
        ...(state ? {} : { bzimage: buffer('vmlinuz'), initrd: buffer('initramfs'), cmdline: CMDLINE }),
        // Read by byte range from R2, on demand, for the life of the VM. See the
        // module comment: this is the option the state size depends on.
        // Supported by v86's constructor, but omitted from its V86Image typings.
        hda: opts.disk,
        // v86 decompresses a state by its zstd magic bytes, not by its name, so
        // the compressed download goes straight in.
        ...(state ? { initial_state: { buffer: state } } : {}),
        uart1: true, // the exec channel, ttyS1
        uart2: true, // the command-done marker, ttyS2
        disable_keyboard: true,
        disable_mouse: true,
        disable_speaker: true,
        autostart: true,
    };
}
export const bootVm = async function bootVm(options) {
    const { manifest, assetBase, wasmUrl, signal } = options;
    const state = manifest.files['state.bin.zst'];
    const restore = options.restore ?? state !== undefined;
    if (restore && state === undefined) {
        throw vmError('manifest', 'this manifest has no saved state, so the VM can only be booted with restore: false');
    }
    if (options.memoryMb !== undefined && restore) {
        throw vmError('fingerprint', 'memoryMb is part of the boot fingerprint, so it may only be passed together with restore: false');
    }
    const memoryMb = options.memoryMb ?? manifest.boot.memoryMb;
    requireSupport(memoryMb);
    const wanted = WHOLE_ASSETS.map((name) => [name, manifest.files[name]]);
    if (restore && state)
        wanted.push(['state.bin.zst', state]);
    else
        for (const name of COLD_BOOT_ASSETS)
            wanted.push([name, manifest.files[name]]);
    const bytesTotal = wanted.reduce((total, [, file]) => total + file.bytes, 0);
    let bytesLoaded = 0;
    const report = (phase, message, fraction) => {
        const progress = { phase, fraction, bytesLoaded, bytesTotal, message };
        options.onProgress?.(progress);
    };
    report('manifest', `Preparing Alpine ${manifest.alpine}…`, 0);
    throwIfAborted(signal);
    if (restore) {
        // A state restored into an emulator built differently misbehaves in ways
        // that look like a guest bug, and the disk is the sharpest edge: a
        // restored guest reads every block it has not written from rootfs.img as
        // it is now. Refuse instead (docs/browser.md, Risks).
        const runtime = await computeBootFingerprint(manifest.files, memoryMb);
        const moved = fingerprintMismatch(runtime, manifest.boot);
        if (moved)
            throw vmError('fingerprint', `the saved state was produced by a different build — ${moved}`);
    }
    report('assets', 'Downloading Linux…', 0);
    const buffers = new Map();
    // Awaited with the machine, not handed to v86 unresolved: a rejection
    // inside v86's constructor is never surfaced, and the boot would hang on
    // the restore screen instead of failing as a download.
    const fetching = { signal, stallMs: options.stallMs ?? STALL_MS };
    const wasm = downloadWasm(wasmUrl, fetching);
    await Promise.all([
        wasm,
        ...wanted.map(async ([name, file]) => {
            const data = await download(assetUrl(assetBase, file), file, fetching, (n) => {
                bytesLoaded += n;
                report('assets', 'Downloading Linux…', (ASSET_SHARE * bytesLoaded) / bytesTotal);
            });
            buffers.set(name, data);
        }),
    ]);
    throwIfAborted(signal);
    report(restore ? 'restore' : 'boot', restore ? 'Restoring the machine…' : 'Booting Linux (no saved state; this takes a while)…', ASSET_SHARE);
    const rootfs = manifest.files['rootfs.img'];
    let emulator;
    let channel;
    const disk = new RangeDisk(assetUrl(assetBase, rootfs), rootfs.bytes, (error) => {
        // Never complete an ATA read with invented bytes. Pause and expose the
        // failure to callers after bounded network retries fail.
        void emulator.stop();
        channel.fail(error.message);
    });
    emulator = new V86(v86Options(manifest, { wasm, disk, memoryMb, buffers }));
    channel = new ExecChannel(emulator);
    const terminal = new GuestTerminal(emulator, channel);
    const commandDone = new CommandDone(emulator);
    const vm = new BrowserVm(manifest, emulator, channel, terminal, commandDone, disk);
    const abandon = async (err) => {
        await vm.destroy();
        throw err;
    };
    const onAbort = () => void vm.destroy();
    signal?.addEventListener('abort', onAbort, { once: true });
    try {
        if (aborted(signal))
            await abandon(vmError('destroyed', 'the boot was aborted'));
        await channel.ready({ restored: restore }).catch((err) => abandon(err));
        report('warm', 'Warming up…', 0.9);
        // A failed warm-up is not a failed boot: `ready()` has already proved the
        // agent answers, and anything actually broken in the guest will fail
        // loudly on the first level load.
        await channel.exec(WARM_SCRIPT, { timeoutMs: WARM_TIMEOUT_MS }).catch(() => undefined);
        if (aborted(signal))
            await abandon(vmError('destroyed', 'the boot was aborted'));
    }
    finally {
        signal?.removeEventListener('abort', onAbort);
    }
    report('ready', 'Ready', 1);
    return vm;
};
class BrowserVm {
    manifest;
    emulator;
    channel;
    terminal;
    commandDone;
    disk;
    destroyed = false;
    constructor(manifest, emulator, channel, terminal, commandDone, disk) {
        this.manifest = manifest;
        this.emulator = emulator;
        this.channel = channel;
        this.terminal = terminal;
        this.commandDone = commandDone;
        this.disk = disk;
    }
    async ready() {
        // `restored: true` skips the EOSREADY banner, which a VM that is already
        // up has long since sent; the probe loop is the part that matters here.
        await this.channel.ready({ restored: true });
    }
    exec(script, opts) {
        return this.channel.exec(script, opts);
    }
    attachTerminal(opts) {
        return this.terminal.attach(opts);
    }
    onCommandDone(cb) {
        return this.commandDone.subscribe(cb);
    }
    onDead(cb) {
        return this.channel.onDead(cb);
    }
    saveState() {
        if (this.destroyed)
            return Promise.reject(vmError('destroyed', 'the VM was destroyed'));
        return this.emulator.save_state();
    }
    async destroy() {
        if (this.destroyed)
            return;
        this.destroyed = true;
        this.disk.destroy();
        this.channel.destroy();
        await this.emulator.destroy().catch(() => undefined);
    }
}
//# sourceMappingURL=boot.js.map